import { useEffect, useMemo, useRef, useState } from 'react'
import { fetchTeamMembers, assignSkillToAgents } from '../api/team'
import { summaryTypeKeys } from '../utils/skillSummary'
import Icon from './Icon'
import styles from './AssignSkillModal.module.css'

// Avatar tint seeded from the member id — same palette/hash as TeamMemberRow so a
// member keeps one colour across the app.
const AVATAR_PALETTE = [
  '#E05A4E', '#E08A4E', '#E0C44E', '#6DBE6D',
  '#4E9EE0', '#7B6DE0', '#C44EBE', '#4EC4C4',
]

function avatarColor(id) {
  const seed = String(id)
  let h = 0
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length]
}

/**
 * AssignSkillModal — assign one skill to one or more team members, opened from the
 * Skill Details screen's Assign button. Members who already hold the skill are shown
 * pre-checked and disabled (they can't be re-assigned); every other member is a
 * toggle. Reuses the SkillDetailsModal visual language and TeamMemberRow pills.
 *
 * Props:
 *   skillId          {number|string} — the skill being assigned
 *   skillName        {string}        — display name (header)
 *   existingAgentIds {Array<number>} — agent ids that already hold the skill
 *   onClose   {function} — dismiss the modal
 *   onAssigned {function} — called after a successful save so the parent can refresh
 */
export default function AssignSkillModal({
  skillId, skillName, existingAgentIds = [], onClose, onAssigned,
}) {
  const [members, setMembers] = useState([])
  const [teams, setTeams] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [search, setSearch] = useState('')
  const [teamFilter, setTeamFilter] = useState('all')

  // Newly-selected agent ids (excludes those already holding the skill).
  const [selected, setSelected] = useState(() => new Set())
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  // Stable set of the already-assigned ids for O(1) membership checks.
  const existing = useMemo(() => new Set(existingAgentIds), [existingAgentIds])

  const didFetch = useRef(false)
  useEffect(() => {
    if (didFetch.current) return
    didFetch.current = true
    fetchTeamMembers()
      .then(data => {
        setTeams(data.teams ?? [])
        setMembers(data.members ?? [])
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false))
  }, [])

  function toggle(id) {
    if (existing.has(id)) return  // already assigned — non-toggleable
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    return members.filter(m => {
      // Prefix-match each name token, mirroring the Team page search.
      const matchesSearch =
        term === '' ||
        m.name.toLowerCase().split(/\s+/).some(part => part.startsWith(term))
      const matchesTeam =
        teamFilter === 'all' || (m.teams ?? []).some(t => String(t.id) === teamFilter)
      return matchesSearch && matchesTeam
    })
  }, [members, search, teamFilter])

  async function handleSave() {
    const ids = [...selected]
    if (ids.length === 0 || saving) return
    setSaving(true)
    setSaveError('')
    try {
      await assignSkillToAgents(skillId, ids)
      onAssigned?.()
      onClose()
    } catch (e) {
      setSaveError(e.message)
    } finally {
      setSaving(false)
    }
  }

  function handleBackdropClick(e) {
    if (e.target === e.currentTarget) onClose()
  }

  const count = selected.size

  return (
    <div className={styles.backdrop} onClick={handleBackdropClick}>
      <div className={styles.modal} role="dialog" aria-modal="true" aria-label="Assign skill">

        <button className={styles.closeBtn} onClick={onClose} aria-label="Close">
          <Icon name="close" />
        </button>

        {/* ── Header ──────────────────────────────────── */}
        <div className={styles.header}>
          <span className={styles.headerIcon}><Icon name="skills" /></span>
          <h2 className={styles.title}>
            Assign <span className={styles.skillName}>{skillName}</span> to a Team Member
          </h2>
        </div>

        {/* ── List header — title + search + team filter ── */}
        <div className={styles.listHeader}>
          <div className={styles.listTitleRow}>
            <span className={styles.listIcon}><Icon name="members" /></span>
            <span className={styles.listTitle}>Team Members</span>
          </div>
          <div className={styles.searchWrap}>
            <span className={styles.searchIcon}><Icon name="search" /></span>
            <input
              className={styles.search}
              type="text"
              placeholder="Search for team member"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Escape' && setSearch('')}
            />
            {search && (
              <button type="button" className={styles.clearSearch} onClick={() => setSearch('')}>
                Clear
              </button>
            )}
          </div>
          <select
            className={styles.filterSelect}
            value={teamFilter}
            onChange={e => setTeamFilter(e.target.value)}
          >
            <option value="all">All Teams</option>
            {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>

        {/* ── Member list — independently scrollable ──── */}
        <div className={styles.list}>
          {loading ? (
            <div className={styles.loadingState}>
              <div className={styles.spinner} />
              <p>Loading team members…</p>
            </div>
          ) : error ? (
            <div className={styles.errorState}>Failed to load: {error}</div>
          ) : filtered.length === 0 ? (
            <div className={styles.empty}>No team members match your search.</div>
          ) : filtered.map(m => {
            const already = existing.has(m.id)
            const checked = already || selected.has(m.id)
            const summary = m.skills_summary ?? {}
            return (
              <div
                key={m.id}
                className={`${styles.row} ${checked ? styles.rowSelected : ''} ${already ? styles.rowDisabled : ''}`}
                onClick={() => toggle(m.id)}
                role="checkbox"
                aria-checked={checked}
                aria-disabled={already}
                tabIndex={already ? -1 : 0}
                onKeyDown={e => {
                  if (!already && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); toggle(m.id) }
                }}
                title={already ? 'Already has this skill' : undefined}
              >
                <span className={`${styles.checkbox} ${checked ? styles.checkboxOn : ''}`}>
                  {checked && <Icon name="check" />}
                </span>

                <div className={styles.avatar} style={{ backgroundColor: avatarColor(m.id) }}>
                  {m.initials}
                </div>

                <span className={styles.memberName}>{m.name}</span>

                <div className={styles.skillsLine}>
                  <span className={styles.skillsIcon}><Icon name="skills" /></span>
                  <span className={styles.totalText}>{summary.Total ?? '0'} total skills:</span>
                  {summaryTypeKeys(summary).map(k => (
                    <span key={k} className={`${styles.pill} ${styles[`pill${k}`]}`}>
                      {summary[k]} {k}
                    </span>
                  ))}
                </div>

                {already && <span className={styles.alreadyTag}>Assigned</span>}
              </div>
            )
          })}
        </div>

        {/* ── Footer ──────────────────────────────────── */}
        <div className={styles.footer}>
          {saveError && <span className={styles.saveError}>{saveError}</span>}
          <button
            className={`${styles.footerBtn} ${styles.footerPrimary}`}
            type="button"
            onClick={handleSave}
            disabled={count === 0 || saving}
          >
            {saving ? 'Saving…' : count === 0 ? 'Save' : `Assign to ${count} member${count !== 1 ? 's' : ''}`}
          </button>
          <button className={styles.footerBtn} type="button" onClick={onClose} disabled={saving}>
            Cancel
          </button>
        </div>

      </div>
    </div>
  )
}
